import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { RiLoader4Line } from 'react-icons/ri';

import Navbar from './components/Navbar';
import Login from './pages/Login';
import PublicRegister from './pages/PublicRegister';
import AdminDashboard from './pages/AdminDashboard';
import UserManagement from './pages/UserManagement';
import CompanyManagement from './pages/CompanyManagement';
import ApplicantManagement from './pages/ApplicantManagement';
import CompanyHRDashboard from './pages/CompanyHRDashboard';
import ApplicantDashboard from './pages/ApplicantDashboard';
import BigScreenView from './pages/BigScreenView';
import Profile from './pages/Profile';

/**
 * Route protection for logged-in users
 */
function ProtectedRoute({ children, allowedRoles, allowedTypes }) {
    const { user, userType, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-[50vh] flex items-center justify-center text-slate-400">
                <RiLoader4Line className="w-8 h-8 animate-spin text-indigo-500 mb-2" />
            </div>
        );
    }

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    // Check userType ('staff' vs 'applicant')
    if (allowedTypes && !allowedTypes.includes(userType)) {
        return <Navigate to="/" replace />;
    }

    // Check staff roles ('admin', 'coordinator', 'company_hr')
    if (allowedRoles && userType === 'staff' && !allowedRoles.includes(user?.role)) {
        return <Navigate to="/" replace />;
    }

    return children;
}

/**
 * Intelligent home page redirect based on logged in user's role
 */
function HomeRedirect() {
    const { user, userType, loading } = useAuth();
    
    if (loading) {
        return (
            <div className="min-h-[50vh] flex items-center justify-center text-slate-400">
                <RiLoader4Line className="w-8 h-8 animate-spin text-indigo-500 mb-2" />
            </div>
        );
    }

    if (!user) return <Navigate to="/login" replace />;

    if (userType === 'applicant') return <Navigate to="/applicant-portal" replace />;
    if (user?.role === 'admin') return <Navigate to="/admin" replace />;
    if (user?.role === 'coordinator') return <Navigate to="/companies" replace />;
    if (user?.role === 'company_hr') return <Navigate to="/hr-stall" replace />;
    
    return <Navigate to="/login" replace />;
}

function MainLayout({ children }) {
    return (
        <div className="min-h-screen flex flex-col">
            <Navbar />
            <main className="flex-1">
                {children}
            </main>
        </div>
    );
}

function App() {
    return (
        <ThemeProvider>
            <AuthProvider>
                <BrowserRouter>
                    <Routes>
                        {/* Fullscreen Venue Display */}
                        <Route path="/big-screen" element={<BigScreenView />} />

                        {/* App Navigation Layout */}
                        <Route path="*" element={
                            <MainLayout>
                                <Routes>
                                    {/* Public Routes */}
                                    <Route path="/" element={<HomeRedirect />} />
                                    <Route path="/login" element={<Login />} />
                                    <Route path="/public-register" element={<PublicRegister />} />

                                    {/* Admin Protected */}
                                    <Route path="/admin" element={
                                        <ProtectedRoute allowedTypes={['staff']} allowedRoles={['admin']}>
                                            <AdminDashboard />
                                        </ProtectedRoute>
                                    } />

                                    {/* Admin Protected Only */}
                                    <Route path="/users" element={
                                        <ProtectedRoute allowedTypes={['staff']} allowedRoles={['admin']}>
                                            <UserManagement />
                                        </ProtectedRoute>
                                    } />

                                    <Route path="/companies" element={
                                        <ProtectedRoute allowedTypes={['staff']} allowedRoles={['admin', 'coordinator']}>
                                            <CompanyManagement />
                                        </ProtectedRoute>
                                    } />

                                    {/* Admin, Coordinator & HR Protected */}
                                    <Route path="/applicants" element={
                                        <ProtectedRoute allowedTypes={['staff']} allowedRoles={['admin', 'coordinator', 'company_hr']}>
                                            <ApplicantManagement />
                                        </ProtectedRoute>
                                    } />

                                    {/* Company HR Protected */}
                                    <Route path="/hr-stall" element={
                                        <ProtectedRoute allowedTypes={['staff']} allowedRoles={['company_hr']}>
                                            <CompanyHRDashboard />
                                        </ProtectedRoute>
                                    } />

                                    {/* Applicant Portal Protected */}
                                    <Route path="/applicant-portal" element={
                                        <ProtectedRoute allowedTypes={['applicant']}>
                                            <ApplicantDashboard />
                                        </ProtectedRoute>
                                    } />

                                    {/* All Logged-in Users & Applicants */}
                                    <Route path="/profile" element={
                                        <ProtectedRoute>
                                            <Profile />
                                        </ProtectedRoute>
                                    } />

                                    {/* Fallback */}
                                    <Route path="*" element={<Navigate to="/" replace />} />
                                </Routes>
                            </MainLayout>
                        } />
                    </Routes>
                </BrowserRouter>
            </AuthProvider>
        </ThemeProvider>
    );
}

const container = document.getElementById('root');
if (container) {
    const root = createRoot(container);
    root.render(<App />);
}
